import React, { useState, useRef, useEffect, useMemo } from 'react'
import axios from 'axios'
import AreaBreakdownTable from './AreaBreakdownTable'
import ComponentRecipeSummary from './ComponentRecipeSummary'
import StatusSegmented from '../../estimating/StatusSegmented'
import PillSelect from '../../estimating/PillSelect'
import PillNumber from '../../estimating/PillNumber'
import EditableList from '../../estimating/EditableList'
import { tokens } from '../../../styles/desertFloraTheme'

const fmt = (v) => '$' + (v || 0).toLocaleString('en-US', { minimumFractionDigits: 0 })

const plainObject = (value) => {
  if (!value) return {}
  if (value instanceof Map) return Object.fromEntries(value.entries())
  return typeof value === 'object' ? value : {}
}

const parseInputValue = (value) => {
  const raw = String(value ?? '').trim()
  const num = Number(raw)
  return raw !== '' && Number.isFinite(num) ? num : raw
}

/* ── Boolean toggle pill (Yes / No) ──────────────────────────────── */
const BooleanToggle = ({ value, onChange }) => {
  const active = !!value
  const pill = (_label, isActive) => ({
    padding: '4px 10px',
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
    background: isActive ? tokens.surface : 'transparent',
    color: isActive ? tokens.ink : tokens.ink3,
    boxShadow: isActive ? '0 1px 2px rgba(28,36,25,.06)' : 'none',
    border: 'none',
    outline: 'none'
  })

  return (
    <span style={{
      display: 'inline-flex',
      background: tokens.bg2,
      borderRadius: 999,
      padding: 2,
      border: '1px solid ' + tokens.line
    }}>
      <button type="button" style={pill('Yes', active)} onClick={() => onChange(true)}>Yes</button>
      <button type="button" style={pill('No', !active)} onClick={() => onChange(false)}>No</button>
    </span>
  )
}

/* ── Dot separator helper ────────────────────────────────────────── */
const Dot = () => (
  <span style={{ color: tokens.ink4, margin: '0 4px', fontSize: 10, lineHeight: 1 }}>&middot;</span>
)

/* ── ConfigureCard ───────────────────────────────────────────────── */
const ConfigureCard = ({ projectService, url, onUpdate }) => {
  const [template, setTemplate] = useState(null)
  const [selections, setSelections] = useState({})
  const [inputValues, setInputValues] = useState({})
  const [saving, setSaving] = useState(false)
  const inputDebounceRef = useRef(null)

  // Fetch template once for both variations + input slots
  useEffect(() => {
    axios.get(`${url}/api/bid-estimation/service-templates`, { params: { serviceId: projectService.serviceId } })
      .then(res => {
        const templates = res.data || []
        const tmpl = templates.find(t => t.serviceId === projectService.serviceId)
        if (!tmpl) return
        setTemplate(tmpl)

        // Initialize variation selections
        if (tmpl.variations && tmpl.variations.length > 0) {
          const saved = projectService.appliedVariations || {}
          const initial = {}
          for (const v of tmpl.variations) {
            initial[v.attributeId] = saved[v.attributeId] !== undefined ? saved[v.attributeId] : v.defaultValue
          }
          setSelections(initial)
        }

        // Initialize input values
        const nextValues = { ...plainObject(projectService.inputValues) }
        const inputSlots = tmpl.inputSlots || []
        inputSlots.forEach(slot => {
          if (!slot?.key) return
          if ((nextValues[slot.key] === undefined || nextValues[slot.key] === null || nextValues[slot.key] === '') && slot.defaultValue != null) {
            nextValues[slot.key] = slot.defaultValue
          }
        })
        setInputValues(nextValues)
      })
      .catch(() => {})
  }, [projectService.serviceId, projectService._id])

  const variations = (template?.variations || [])
  const inputSlots = (template?.inputSlots || []).filter(slot => slot.key && slot.key !== 'assembly_qty' && slot.key !== 'assembly_unit')
  const optionCount = variations.length
  const inputCount = inputSlots.length

  // Nothing to configure
  if (!template || (optionCount === 0 && inputCount === 0)) return null

  // Save variation change (instant)
  const handleVariationChange = async (attributeId, value) => {
    const next = { ...selections, [attributeId]: value }
    setSelections(next)
    setSaving(true)
    try {
      await axios.put(`${url}/api/bid-estimation/project-services/${projectService._id}/variations`, { variations: next })
      onUpdate()
    } catch (e) {
      console.error('Variation update failed:', e)
    } finally {
      setSaving(false)
    }
  }

  // Save input change (debounced 300ms for numeric, instant for others)
  const saveInput = async (key, rawValue) => {
    const nextInputValues = {
      ...plainObject(projectService.inputValues),
      ...inputValues,
      [key]: parseInputValue(rawValue)
    }
    setSaving(true)
    try {
      await axios.put(`${url}/api/bid-estimation/project-services/${projectService._id}`, { inputValues: nextInputValues })
      onUpdate()
    } catch (e) {
      console.error('Recipe input update failed:', e)
    } finally {
      setSaving(false)
    }
  }

  const handleInputChange = (key, value) => {
    setInputValues(prev => ({ ...prev, [key]: value }))
    if (inputDebounceRef.current) clearTimeout(inputDebounceRef.current)
    inputDebounceRef.current = setTimeout(() => saveInput(key, value), 300)
  }

  // Merge variations + inputs into a single list of row descriptors
  const rows = []
  variations.forEach(v => {
    rows.push({ kind: 'variation', data: v })
  })
  inputSlots.forEach(slot => {
    rows.push({ kind: 'input', data: slot })
  })

  return (
    <div style={{
      background: tokens.surface,
      border: '1px solid ' + tokens.line,
      borderRadius: 14,
      overflow: 'hidden',
      marginBottom: 20
    }}>
      {/* Sand header */}
      <div style={{
        background: tokens.surface2,
        padding: '12px 16px',
        borderBottom: '1px solid ' + tokens.line,
        display: 'flex',
        alignItems: 'center'
      }}>
        <span style={{ fontSize: 14, fontWeight: 600, color: tokens.ink }}>Configure</span>
        {optionCount > 0 && (
          <>
            <Dot />
            <span style={{ fontSize: 12, color: tokens.ink3 }}>{optionCount} option{optionCount !== 1 ? 's' : ''}</span>
          </>
        )}
        {inputCount > 0 && (
          <>
            <Dot />
            <span style={{ fontSize: 12, color: tokens.ink3 }}>{inputCount} input{inputCount !== 1 ? 's' : ''}</span>
          </>
        )}
        {saving && (
          <>
            <Dot />
            <span style={{ fontSize: 12, color: tokens.accent, fontWeight: 500 }}>Saving...</span>
          </>
        )}
      </div>

      {/* Body rows */}
      <div style={{ padding: '14px 16px' }}>
        {rows.map((row, idx) => {
          const isLast = idx === rows.length - 1

          const rowStyle = {
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '10px 0',
            borderBottom: isLast ? 'none' : '1px dashed ' + tokens.line
          }

          const labelStyle = {
            fontSize: 13,
            fontWeight: 600,
            color: tokens.ink,
            minWidth: 140,
            flexShrink: 0
          }

          if (row.kind === 'variation') {
            const v = row.data
            const val = selections[v.attributeId]

            if (v.type === 'boolean') {
              return (
                <div key={v.attributeId} style={rowStyle}>
                  <span style={labelStyle}>{v.label}</span>
                  <BooleanToggle value={val} onChange={(newVal) => handleVariationChange(v.attributeId, newVal)} />
                  {v.helperText && <span style={{ fontSize: 12, color: tokens.ink3 }}>{v.helperText}</span>}
                </div>
              )
            }

            if (v.type === 'range') {
              return (
                <div key={v.attributeId} style={rowStyle}>
                  <span style={labelStyle}>{v.label}</span>
                  <PillNumber
                    value={val ?? v.min ?? ''}
                    onChange={(newVal) => handleVariationChange(v.attributeId, Number(newVal))}
                    unit={v.unit || ''}
                  />
                  {v.helperText && <span style={{ fontSize: 12, color: tokens.ink3 }}>{v.helperText}</span>}
                </div>
              )
            }

            // select type
            return (
              <div key={v.attributeId} style={rowStyle}>
                <span style={labelStyle}>{v.label}</span>
                <PillSelect
                  options={v.options || []}
                  value={val || ''}
                  onChange={(newVal) => handleVariationChange(v.attributeId, newVal)}
                />
                {v.helperText && <span style={{ fontSize: 12, color: tokens.ink3 }}>{v.helperText}</span>}
                {/* Show the description of the selected option as trailing helper */}
                {!v.helperText && (() => {
                  const selectedOpt = (v.options || []).find(o => o.value === val)
                  return selectedOpt?.description
                    ? <span style={{ fontSize: 12, color: tokens.ink3 }}>{selectedOpt.description}</span>
                    : null
                })()}
              </div>
            )
          }

          // input kind
          const slot = row.data
          const value = inputValues[slot.key] ?? ''
          const required = !(slot.tags || []).includes('optional') && slot.defaultValue == null

          return (
            <div key={slot.key} style={rowStyle}>
              <span style={labelStyle}>{slot.label || slot.key}</span>
              <PillNumber
                value={value}
                onChange={(newVal) => handleInputChange(slot.key, newVal)}
                unit={slot.unit || ''}
                required={required}
              />
              {slot.helperText && <span style={{ fontSize: 12, color: tokens.ink3 }}>{slot.helperText}</span>}
            </div>
          )
        })}
      </div>
    </div>
  )
}

/* ── ServiceDetail ───────────────────────────────────────────────── */
const ServiceDetail = ({ projectService, project, url, onUpdate }) => {
  const [pricingFlag, setPricingFlag] = useState('tbd')
  const [exclusions, setExclusions] = useState(() => project?.exclusions || [])
  const [assumptions, setAssumptions] = useState(() => project?.assumptions || [])

  const hasAreas = (projectService.areas || []).length > 0
  const areaColumnLabel = projectService.source === 'scope' ? 'Source Item' : 'Area'

  // Compute cost/sell derived values
  const totalQty = projectService.totalQuantity || 0
  const totalCost = projectService.totalCost || 0
  const totalSell = projectService.totalSell || 0
  const unitCost = totalQty > 0 ? totalCost / totalQty : 0
  const markupPct = totalCost > 0 ? Math.round(((totalSell - totalCost) / totalCost) * 100) : 0
  const unit = projectService.unit || 'EA'

  // Confidence score: 5 possible points
  const confidenceScore = useMemo(() => {
    let score = 0
    if (pricingFlag === 'firm') score += 1
    score += 1
    const components = projectService.components || []
    if (components.some(c => c.catalogItemId)) score += 1
    if ((projectService.areas || []).length > 0) score += 1
    if (!components.some(c => (c.markupOverride || 0) > 25)) score += 1
    return score
  }, [pricingFlag, projectService.components, projectService.areas])

  return (
    <div>
      {/* Service header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
        <div>
          <div style={{
            fontFamily: tokens.fontMono,
            fontSize: 10.5,
            fontWeight: 600,
            letterSpacing: '.12em',
            textTransform: 'uppercase',
            color: tokens.ink3
          }}>
            {projectService.serviceCategory}
          </div>
          <div style={{
            fontFamily: "'Instrument Serif', Georgia, serif",
            fontSize: 28,
            fontWeight: 400,
            color: tokens.ink,
            marginTop: 4,
            lineHeight: 1.15
          }}>
            {projectService.serviceName}
          </div>
          <div style={{
            fontFamily: tokens.fontMono,
            fontSize: 11,
            color: tokens.ink4,
            marginTop: 4
          }}>
            {projectService.serviceKey || projectService.serviceId}
            {projectService.templateName && (
              <span> &middot; {projectService.templateName}</span>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 4 }}>
          <button
            type="button"
            style={{
              background: 'transparent',
              border: '1px solid transparent',
              color: tokens.ink3,
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
              padding: '5px 10px',
              borderRadius: 6,
              transition: 'background 0.12s'
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(28,36,25,.05)' }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent' }}
          >
            Duplicate
          </button>
          <button
            type="button"
            style={{
              background: 'transparent',
              border: '1px solid transparent',
              color: tokens.ink3,
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
              padding: '5px 10px',
              borderRadius: 6,
              transition: 'background 0.12s'
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(28,36,25,.05)' }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent' }}
          >
            Remove
          </button>
        </div>
      </div>

      {/* Cost / Sell / Qty strip */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: 14,
        margin: '20px 0'
      }}>
        {/* Quantity card */}
        <div style={{
          background: tokens.surface2,
          border: '1px solid ' + tokens.line,
          borderRadius: 14,
          padding: '14px 16px'
        }}>
          <div style={{
            fontFamily: tokens.fontMono,
            fontSize: 10.5,
            fontWeight: 600,
            letterSpacing: '.12em',
            textTransform: 'uppercase',
            color: tokens.ink3
          }}>
            Quantity
          </div>
          <div style={{
            fontFamily: tokens.fontMono,
            fontSize: 22,
            fontWeight: 600,
            marginTop: 6,
            color: tokens.ink
          }}>
            {totalQty.toLocaleString('en-US')} {unit}
          </div>
          <div style={{ fontSize: 12, color: tokens.ink3, marginTop: 4 }}>
            {pricingFlag === 'allowance'
              ? 'Allowance — confirm with field measurements'
              : `${fmt(unitCost)} / ${unit} unit cost`
            }
          </div>
        </div>

        {/* Cost card */}
        <div style={{
          background: tokens.surface2,
          border: '1px solid ' + tokens.line,
          borderRadius: 14,
          padding: '14px 16px'
        }}>
          <div style={{
            fontFamily: tokens.fontMono,
            fontSize: 10.5,
            fontWeight: 600,
            letterSpacing: '.12em',
            textTransform: 'uppercase',
            color: tokens.ink3
          }}>
            Cost
          </div>
          <div style={{
            fontFamily: tokens.fontMono,
            fontSize: 22,
            fontWeight: 600,
            marginTop: 6,
            color: tokens.ink
          }}>
            {fmt(totalCost)}
          </div>
          <div style={{ fontSize: 12, color: tokens.ink3, marginTop: 4 }}>
            {fmt(unitCost)} / {unit} unit cost
          </div>
        </div>

        {/* Sell card */}
        <div style={{
          background: tokens.surface2,
          border: '1px solid ' + tokens.line,
          borderRadius: 14,
          padding: '14px 16px'
        }}>
          <div style={{
            fontFamily: tokens.fontMono,
            fontSize: 10.5,
            fontWeight: 600,
            letterSpacing: '.12em',
            textTransform: 'uppercase',
            color: tokens.ink3
          }}>
            Sell
          </div>
          <div style={{
            fontFamily: tokens.fontMono,
            fontSize: 22,
            fontWeight: 600,
            marginTop: 6,
            color: tokens.primary
          }}>
            {fmt(totalSell)}
          </div>
          <div style={{ fontSize: 12, color: tokens.ink3, marginTop: 4 }}>
            {markupPct}% markup applied
          </div>
        </div>
      </div>

      {/* Service-level controls bar */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 16,
        marginBottom: 20
      }}>
        <StatusSegmented value={pricingFlag} onChange={setPricingFlag} />

        {/* Production rate override placeholder */}
        <div style={{
          border: '1px dashed ' + tokens.line2,
          borderRadius: 999,
          padding: '4px 12px',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4
        }}>
          <span style={{
            fontFamily: tokens.fontMono,
            fontSize: 13,
            color: tokens.ink3
          }}>
            &mdash;
          </span>
          <span style={{ fontSize: 12, color: tokens.ink3 }}>
            hr / unit
          </span>
        </div>

        {/* Confidence indicator */}
        <div style={{
          marginLeft: 'auto',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 3
        }}>
          {[0, 1, 2, 3, 4].map((i) => (
            <div
              key={i}
              style={{
                width: 10,
                height: 10,
                borderRadius: 2,
                background: i < confidenceScore ? tokens.primary : tokens.line
              }}
            />
          ))}
        </div>
      </div>

      {/* Configure card — merged variations + recipe inputs */}
      <ConfigureCard projectService={projectService} url={url} onUpdate={onUpdate} />

      {/* Areas card — always visible */}
      <div style={{
        background: tokens.surface,
        border: '1px solid ' + tokens.line,
        borderRadius: 14,
        overflow: 'hidden',
        marginBottom: 20
      }}>
        {/* Sand header */}
        <div style={{
          background: tokens.surface2,
          padding: '12px 16px',
          borderBottom: '1px solid ' + tokens.line,
          display: 'flex',
          alignItems: 'center'
        }}>
          <span style={{ fontSize: 14, fontWeight: 600, color: tokens.ink }}>Areas</span>
          <Dot />
          <span style={{ fontSize: 12, color: tokens.ink3 }}>
            {hasAreas ? `${projectService.areas.length} area${projectService.areas.length !== 1 ? 's' : ''}` : '0 areas'}
          </span>
          {projectService.assemblyName && (
            <>
              <Dot />
              <span style={{ fontSize: 11, color: tokens.ink4 }}>synced from {projectService.assemblyName}</span>
            </>
          )}
        </div>
        <AreaBreakdownTable projectService={projectService} url={url} onUpdate={onUpdate} areaColumnLabel={areaColumnLabel} />
      </div>

      <ComponentRecipeSummary projectService={projectService} project={project} url={url} onUpdate={onUpdate} />

      {/* Exclusions & Assumptions */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 14,
        marginTop: 20
      }}>
        <div style={{
          background: tokens.surface,
          border: '1px solid ' + tokens.line,
          borderRadius: 14,
          overflow: 'hidden'
        }}>
          <div style={{
            background: tokens.surface2,
            padding: '12px 16px',
            borderBottom: '1px solid ' + tokens.line,
            display: 'flex',
            alignItems: 'center',
            gap: 6
          }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: tokens.ink }}>Exclusions</span>
            {exclusions.length > 0 && (
              <span style={{
                fontFamily: tokens.fontMono,
                fontSize: 10.5,
                fontWeight: 600,
                color: tokens.ink3,
                background: tokens.bg2,
                border: '1px solid ' + tokens.line,
                borderRadius: 999,
                padding: '1px 7px'
              }}>
                {exclusions.length}
              </span>
            )}
          </div>
          <div style={{ padding: '10px 16px' }}>
            <EditableList
              items={exclusions}
              onChange={setExclusions}
              addLabel="Add exclusion"
            />
          </div>
        </div>

        <div style={{
          background: tokens.surface,
          border: '1px solid ' + tokens.line,
          borderRadius: 14,
          overflow: 'hidden'
        }}>
          <div style={{
            background: tokens.surface2,
            padding: '12px 16px',
            borderBottom: '1px solid ' + tokens.line,
            display: 'flex',
            alignItems: 'center',
            gap: 6
          }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: tokens.ink }}>Assumptions</span>
            {assumptions.length > 0 && (
              <span style={{
                fontFamily: tokens.fontMono,
                fontSize: 10.5,
                fontWeight: 600,
                color: tokens.ink3,
                background: tokens.bg2,
                border: '1px solid ' + tokens.line,
                borderRadius: 999,
                padding: '1px 7px'
              }}>
                {assumptions.length}
              </span>
            )}
          </div>
          <div style={{ padding: '10px 16px' }}>
            <EditableList
              items={assumptions}
              onChange={setAssumptions}
              addLabel="Add assumption"
            />
          </div>
        </div>
      </div>
    </div>
  )
}

export default ServiceDetail
